import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  TextField,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { useEffect, useState, useCallback } from "react";
import { BiX } from "react-icons/bi";
import { useTranslation } from "react-i18next";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import API_URLS from "../../../constants/apiConstants";
import "../css/RoomModal.css"; // reuse same RoomModal style
import useAuth from "../../../hooks/useAuth";
import { DeleteConfirmationDialog } from "./DeleteConfirmationDialog";

export const EventModal = ({
  open,
  onClose,
  event = null, // null --> add
  selectedDate,
  nurseryId,
  onSaved,
}) => {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const { school } = useAuth();

  const isEdit = Boolean(event);

  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [errors, setErrors] = useState({});
  const [rooms, setRooms] = useState([]);

  const [form, setForm] = useState({
    title: "",
    description: "",
    rooms: [],
    start_datetime: "",
    end_datetime: "",
  });

  const resetForm = useCallback(() => {
    setForm({
      title: "",
      description: "",
      rooms: [],
      start_datetime: "",
      end_datetime: "",
    });
    setErrors({});
  }, []);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  // Helper function to tell if the form changed to enable the save button
  const isFormChanged = () => {
    if (!isEdit) return true; // Add mode → always allow save

    const eventRoomIds = Array.isArray(event.rooms) 
      ? event.rooms.map(r => r.id || r) 
      : (event.room ? [event.room] : []);

    return (
      form.title !== (event.title || "") ||
      form.description !== (event.description || "") ||
      JSON.stringify(form.rooms.sort((a, b) => a - b)) !== JSON.stringify(eventRoomIds.sort((a, b) => a - b)) ||
      form.start_datetime !== (event.start_datetime?.slice(0, 16) || "") ||
      form.end_datetime !== (event.end_datetime?.slice(0, 16) || "")
    );
  };

  const validateForm = () => {
    const newErrors = {};

    if (!form.title.trim()) newErrors.title = t("forms.required_field");
    if (!form.rooms || form.rooms.length === 0) newErrors.rooms = t("forms.required_field");
    if (!form.start_datetime)
      newErrors.start_datetime = t("forms.required_field");
    if (!form.end_datetime) newErrors.end_datetime = t("forms.required_field");

    if (
      form.start_datetime &&
      form.end_datetime &&
      form.end_datetime < form.start_datetime
    ) {
      newErrors.end_datetime = t("forms.add_event.end_before_start");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const payload = {
        nursery: nurseryId,
        title: form.title,
        description: form.description,
        rooms: form.rooms,
        start_datetime: form.start_datetime,
        end_datetime: form.end_datetime,
      };

      let eventsUrl = API_URLS.events(nurseryId);
      const url = isEdit ? `${eventsUrl}${event.id}/` : eventsUrl;
      const method = isEdit ? "patch" : "post";
      await axiosPrivate[method](url, payload);

      onSaved?.();
      handleClose();
    } catch (err) {
      console.error(err);
      setErrors({
        server: err.response?.data?.message || t("titles.something_went_wrong"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    setIsLoading(true);

    try {
      let eventsUrl = API_URLS.events(nurseryId);
      const url = `${eventsUrl}${event.id}/`;
      await axiosPrivate.delete(url);
      onSaved?.();
      handleClose();
    } catch (err) {
      console.error(err);
      setErrors({
        server: err.response?.data?.message || t("titles.something_went_wrong"),
      });
    } finally {
      setIsLoading(false);
      setIsDeleting(false);
      setShowDeleteConfirmation(false);
    }
  };

  useEffect(() => {
    if (open) {
      if (isEdit && event) {
        // Edit-mode
        const eventRoomIds = Array.isArray(event.rooms) 
          ? event.rooms.map(r => r.id || r) 
          : (event.room ? [event.room] : []);
        
        setForm({
          title: event.title || "",
          description: event.description || "",
          rooms: eventRoomIds,
          start_datetime: event.start_datetime?.slice(0, 16) || "",
          end_datetime: event.end_datetime?.slice(0, 16) || "",
        });
      } else {
        // Add-mode
        const now = new Date();
        const selected = selectedDate
          ? new Date(selectedDate.format("YYYY-MM-DD"))
          : new Date();

        // start = selected date + current time
        selected.setHours(now.getHours(), now.getMinutes(), 0, 0);

        // Format for datetime-local input (local time, not UTC)
        const formatLocalDateTime = (date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          return `${year}-${month}-${day}T${hours}:${minutes}`;
        };

        setForm({
          title: "",
          description: "",
          rooms: [],
          start_datetime: formatLocalDateTime(selected),
          end_datetime: "",
        });

        setErrors({});
      }
    }
  }, [open, isEdit, event, selectedDate]);

  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const api_url = API_URLS.getSchoolRooms(school.id);
        const response = await axiosPrivate.get(api_url);
        const data = response?.data?.map((room) => ({
          value: room.id,
          label: room.name,
        }));

        setRooms(data);
      } catch (error) {
        // Do nothing
      }
    };

    fetchRooms();
  }, [axiosPrivate, school.id]);

  return (
    <Modal open={open} onClose={handleClose}>
      <Box className="modalStyle">
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={4}
        >
          {
            <Typography variant="h5" fontWeight="bold" color="secondary">
              {isEdit
                ? t("admin_pages.calendar.event")
                : t("admin_pages.calendar.add")}
            </Typography>
          }
          <IconButton onClick={handleClose} size="small">
            <BiX />
          </IconButton>
        </Box>

        {/* Title */}
        <Box mb={3}>
          <Typography
            component="label"
            display="block"
            color="secondary"
            gutterBottom
          >
            {t("forms.add_event.title")} <span className="text-danger">*</span>
          </Typography>
          <input
            type="text"
            value={form.title}
            className={`form-control ${errors.title ? "is-invalid" : ""}`}
            onChange={(e) => handleChange("title", e.target.value)}
            disabled={isLoading}
          />
          {errors.title && (
            <Typography className="errorText mt-1">{errors.title}</Typography>
          )}
        </Box>

        {/* Description */}
        <Box mb={3}>
          <Typography
            component="label"
            display="block"
            color="secondary"
            gutterBottom
          >
            {t("forms.add_event.description")}
          </Typography>
          <textarea
            type="text"
            rows={2}
            value={form.description}
            className={`form-control ${errors.description ? "is-invalid" : ""}`}
            onChange={(e) => handleChange("description", e.target.value)}
            disabled={isLoading}
          />
          {errors.description && (
            <Typography className="errorText mt-1">
              {errors.description}
            </Typography>
          )}
        </Box>

        {/* Rooms */}
        <Box mb={3}>
          <Typography color="secondary" gutterBottom>
            {t("forms.add_event.room")} <span className="text-danger">*</span>
          </Typography>
          <FormControl fullWidth size="small" error={!!errors.rooms}>
            <Select
              multiple
              value={form.rooms}
              displayEmpty
              onChange={(e) => handleChange("rooms", e.target.value)}
            >
              {rooms.map((room) => (
                <MenuItem key={room.value} value={room.value}>
                  {room.label}
                </MenuItem>
              ))}
            </Select>
            {errors.rooms && (
              <Typography className="errorText mt-1">
                {errors.rooms}
              </Typography>
            )}
          </FormControl>
        </Box>

        {/* Start */}
        <Box mb={3}>
          <Typography color="secondary" gutterBottom>
            {t("forms.add_event.start_datetime")}{" "}
            <span className="text-danger">*</span>
          </Typography>
          <TextField
            type="datetime-local"
            fullWidth
            size="small"
            value={form.start_datetime}
            onChange={(e) => handleChange("start_datetime", e.target.value)}
            error={Boolean(errors.start_datetime)}
          />
          {errors.start_datetime && (
            <Typography className="errorText mt-1">
              {errors.start_datetime}
            </Typography>
          )}
        </Box>

        {/* End */}
        <Box mb={4}>
          <Typography color="secondary" gutterBottom>
            {t("forms.add_event.end_datetime")}{" "}
            <span className="text-danger">*</span>
          </Typography>
          <TextField
            type="datetime-local"
            fullWidth
            size="small"
            value={form.end_datetime}
            slotProps={{
              htmlInput: {
                min: form.start_datetime,
              },
            }}
            onChange={(e) => handleChange("end_datetime", e.target.value)}
            error={Boolean(errors.end_datetime)}
          />
          {errors.end_datetime && (
            <Typography className="errorText mt-1">
              {errors.end_datetime}
            </Typography>
          )}
        </Box>

        {errors.server && (
          <Typography className="errorText" mb={2}>
            {errors.server}
          </Typography>
        )}

        {/* Footer */}
        <Box display="flex" justifyContent="space-between">
          {isEdit && (
            <button
              className="btn btn-danger submitButton"
              onClick={() => setShowDeleteConfirmation(true)}
              disabled={isLoading}
            >
              {isDeleting ? t("room_modal.processing") : t("actions.delete")}
            </button>
          )}

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isLoading || !isFormChanged()}
            startIcon={
              isLoading ? <CircularProgress size={20} color="inherit" /> : null
            }
            className="submitButton"
          >
            {isLoading
              ? t("room_modal.processing")
              : t(isEdit ? "actions.save" : "actions.add")}
          </Button>
        </Box>
        {showDeleteConfirmation && (
          <DeleteConfirmationDialog
            message={t("admin_pages.calendar.delete_event_confirmation", {
              eventTitle: event.title,
            })}
            onDelete={handleDelete}
            onCancel={() => setShowDeleteConfirmation(false)}
          />
        )}
      </Box>
    </Modal>
  );
};
